import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHome,
    faChevronLeft,
    faBookOpenReader,
    faClipboardQuestion,
    faObjectGroup,
    faUsers,
    faChartPie,
    faPowerOff,
    faBars,
} from '@fortawesome/free-solid-svg-icons';
import { useGlobal } from '../../state';
import { toTitleCase } from '../../lib/utils';
import { Avatar, Button, Tooltip } from '@heroui/react';
import { auth } from '../../config/firebaseConfig';
import { useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { useMedia } from '../hooks/use-media';

const Sidebar = ({ children }) => {
    const [{ user, brand, finishedOnboarding }, dispatch] = useGlobal();
    const currentAuthenticatedUser = auth.currentUser;
    const isHighRes = useMedia('md');
    const [isOpen, setIsOpen] = useState(isHighRes ? true : false);
    const navigate = useNavigate();
    const location = useLocation();

    const toggleSidebar = () => {
        setIsOpen(!isOpen);

        dispatch({ type: 'IS_SIDEBAR_OPEN', isSidebarOpen: !isOpen });
    };

    const categories = [
        {
            category: 'Privire de Ansamblu',
            items: [
                {
                    visible: true,
                    id: 'home',
                    label: 'Acasă',
                    icon: faHome,
                    link: '/dashboard',
                },
                {
                    visible: user?.role === 'manager' || user?.role == 'admin' ? true : false,
                    id: 'manageUsers',
                    label: 'Utilizatori',
                    icon: faUsers,
                    link: '/users',
                },
                {
                    visible: false,
                    id: 'statistics',
                    label: 'Statistici',
                    icon: faChartPie,
                    link: '/statistics',
                },
            ],
        },
        {
            category: 'Instruire',
            items: [
                {
                    visible: user?.role === 'manager' ? true : false,
                    id: 'courses',
                    label: 'Cursurile mele',
                    icon: faBookOpenReader,
                    link: '/manager-courses',
                },
                {
                    visible: user?.role === 'admin' ? true : false,
                    id: 'editCourse',
                    label: 'Cursuri',
                    icon: faBookOpenReader,
                    link: '/courses',
                },
                {
                    visible: user?.role === 'admin' ? true : false,
                    id: 'createQuestionnaire',
                    label: 'Chestionare',
                    icon: faClipboardQuestion,
                    link: '/questionnaires',
                },
                {
                    visible: finishedOnboarding || user?.role == 'admin',
                    id: 'learningPlans',
                    label: 'Planuri de Invățare',
                    icon: faObjectGroup,
                    link: '/learning-plans',
                },
            ],
        },
    ];

    const logOut = async () => {
        try {
            await signOut(auth);

            navigate('/');
        } catch (err) {
            console.log(err);
        }
    };

    return (
        <div
            className={`flex ${isHighRes ? 'flex-row' : 'flex-col'} h-screen relative ${
                isOpen ? '' : `${isHighRes ? 'divide-x divide-secondary-800' : ''}`
            }`}
        >
            {isHighRes || (!isHighRes && isOpen) ? (
                <div
                    className={`flex flex-col bg-secondary transition-[width] duration-500 ${
                        isHighRes ? '' : 'absolute z-50 h-screen'
                    } ${isOpen ? 'w-72 md:w-56 lg:w-60 2xl:w-72 p-6' : 'w-16 py-6 px-2'}`}
                >
                    <div
                        className={`flex items-center ${
                            isOpen ? 'gap-3 px-3 justify-start' : 'gap-0 px-0 justify-center'
                        }`}
                    >
                        <Avatar
                            classNames={{
                                base: ['bg-white'],
                                img: ['object-contain'],
                            }}
                            size="sm"
                            src={`/logos/${
                                brand ? brand : user?.role == 'admin' ? 'city grill' : user?.job_description?.brand
                            }.png`}
                        />

                        <h1 className={`text-small font-bold text-white ${isOpen ? 'block' : 'hidden'}`}>
                            {brand
                                ? brand
                                : user?.role == 'admin'
                                ? 'city grill'
                                : toTitleCase(user?.job_description?.brand)}
                        </h1>
                    </div>

                    <span aria-hidden="true" className="w-px h-px block pl-1 pt-8"></span>

                    <div
                        className={`flex items-center ${
                            isOpen ? 'gap-3 px-3 justify-start' : 'gap-0 px-0 justify-center'
                        }`}
                    >
                        <Avatar
                            classNames={{
                                base: ['bg-white'],
                                img: ['object-contain'],
                            }}
                            size="sm"
                            name={currentAuthenticatedUser?.email?.charAt(0).toUpperCase()}
                        />

                        <div className={`flex flex-col text-white ${isOpen ? 'block' : 'hidden'}`}>
                            <h1 className="text-small font-medium">
                                {toTitleCase(currentAuthenticatedUser?.email.split('@')[0])}
                            </h1>

                            <h1 className="text-tiny text-gray-500">{toTitleCase(user?.role)}</h1>
                        </div>
                    </div>

                    <div className="overflow-y-auto data-[top-scroll=true]:[mask-image:linear-gradient(0deg,#000_calc(100%_-_var(--scroll-shadow-size)),transparent)] data-[bottom-scroll=true]:[mask-image:linear-gradient(180deg,#000_calc(100%_-_var(--scroll-shadow-size)),transparent)] data-[top-bottom-scroll=true]:[mask-image:linear-gradient(#000,#000,transparent_0,#000_var(--scroll-shadow-size),#000_calc(100%_-_var(--scroll-shadow-size)),transparent)] -mr-6 h-full max-h-full py-6 pr-6">
                        <div className="w-full relative flex flex-col gap-1 p-1 overflow-clip list-none">
                            <nav className="w-full flex flex-col gap-0.5 outline-none items-center">
                                {categories.map((category, index) => {
                                    return (
                                        <li key={index} className={`relative w-full ${isOpen ? 'mb-2' : 'mb-0'}`}>
                                            <div className={`text-tiny text-white pl-1 ${isOpen ? 'block' : 'hidden'}`}>
                                                {(finishedOnboarding ||
                                                    user?.role == 'admin' ||
                                                    user?.role == 'manager' ||
                                                    category.category === 'Privire de Ansamblu') &&
                                                    category.category}
                                            </div>

                                            <ul className={isOpen ? 'pt-1' : ''}>
                                                {category.items.map((item, index) => {
                                                    return (
                                                        item.visible && (
                                                            <Tooltip
                                                                delay={250}
                                                                closeDelay={0}
                                                                key={category.category + index}
                                                                color="primary"
                                                                content={item.label}
                                                                placement="right"
                                                                isDisabled={isOpen}
                                                            >
                                                                <Button
                                                                    className={`flex items-center w-full text-gray-200 bg-transparent hover:bg-secondary-400 ${
                                                                        location.pathname.includes(item.link)
                                                                            ? 'bg-secondary-400 text-white font-bold'
                                                                            : ''
                                                                    } ${
                                                                        isOpen ? 'justify-start' : 'justify-center mt-1'
                                                                    }`}
                                                                    onPress={() => navigate(item.link)}
                                                                    color="primary"
                                                                    variant="solid"
                                                                    isIconOnly={!isOpen}
                                                                    size="md"
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={item.icon}
                                                                        className={isOpen ? 'mr-2' : ''}
                                                                    />

                                                                    <span
                                                                        className={`${
                                                                            isOpen ? 'visible' : 'invisible w-0'
                                                                        }`}
                                                                    >
                                                                        {item.label}
                                                                    </span>
                                                                </Button>
                                                            </Tooltip>
                                                        )
                                                    );
                                                })}

                                                {!isOpen && index === 0 && (
                                                    <li className="shrink-0 bg-secondary-400 border-none w-full h-divider mt-2"></li>
                                                )}
                                            </ul>
                                        </li>
                                    );
                                })}
                            </nav>
                        </div>
                    </div>

                    <div className={`flex flex-col ${isOpen ? 'justify-end' : 'justify-center'} items-center mt-auto`}>
                        <Tooltip
                            delay={250}
                            closeDelay={0}
                            color="primary"
                            content="Deconecteaza-te"
                            placement="right"
                            isDisabled={isOpen}
                        >
                            <Button
                                className={`flex items-center w-full text-gray-200 bg-transparent hover:bg-secondary-400 ${
                                    isOpen ? 'justify-start' : 'justify-center mt-1'
                                }`}
                                onPress={logOut}
                                color="primary"
                                variant="solid"
                                isIconOnly={!isOpen}
                                size="md"
                            >
                                <FontAwesomeIcon className={isOpen ? 'mr-2' : ''} icon={faPowerOff} />

                                <span className={`${isOpen ? 'visible' : 'invisible w-0'}`}>Deconecteaza-te</span>
                            </Button>
                        </Tooltip>

                        <Tooltip
                            delay={250}
                            closeDelay={0}
                            color="primary"
                            content="Inchide Meniul"
                            placement="right"
                            isDisabled={isOpen}
                        >
                            <Button
                                className={`flex items-center w-full text-gray-200 bg-transparent hover:bg-secondary-400 ${
                                    isOpen ? 'justify-start' : 'justify-center mt-1'
                                }`}
                                color="primary"
                                variant="solid"
                                isIconOnly={!isOpen}
                                size="md"
                                onPress={toggleSidebar}
                            >
                                <FontAwesomeIcon
                                    className={`transition-all duration-300 ${isOpen ? 'mr-2' : 'rotate-180 mr-0'}`}
                                    icon={faChevronLeft}
                                />

                                <span className={`${isOpen ? 'visible' : 'invisible w-0'}`}>Inchide Meniul</span>
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            ) : (
                <div className="flex justify-between items-center w-full px-6 py-3 bg-secondary absolute top-0 z-30 border-b border-secondary-800">
                    <Button onPress={() => setIsOpen(true)} isIconOnly>
                        <FontAwesomeIcon icon={faBars} />
                    </Button>

                    <Avatar
                        classNames={{
                            base: ['bg-white'],
                            img: ['object-contain'],
                        }}
                        src={`/logos/${
                            brand ? brand : user?.role == 'admin' ? 'city grill' : user?.job_description?.brand
                        }.png`}
                    />
                </div>
            )}

            {!isHighRes && isOpen && (
                <div onClick={() => setIsOpen(false)} className="h-screen w-screen bg-black/40 absolute z-40"></div>
            )}

            <div
                id="scrollParent"
                className={`flex-1 min-h-screen shadow-lg bg-secondary transition-all duration-300 ${
                    isHighRes
                        ? 'overflow-y-auto p-4'
                        : `pt-[66px] ${isOpen ? 'overflow-y-hidden blur-sm' : 'overflow-y-auto blur-none'}`
                }`}
            >
                <div className="min-h-full bg-white rounded-lg">{children}</div>
            </div>
        </div>
    );
};

export default Sidebar;
